// Scout schools domain — types + display helpers shared by the Schools
// table and the SchoolDrawer. Mirrors scout-dashboard's data model: docs are
// written by both the Python merge pipeline (data_quality_score 0–1) and
// older imports (0–100), so qualityPct normalizes exactly like
// scout-dashboard's QualityBar does.

export type Coach = {
  name?: string;
  sport?: string;
  sport_id?: string;
  role?: string;
  title?: string;
  email?: string;
  phone?: string;
};

export type AthleticDirector = {
  name?: string;
  email?: string | null;
  phone?: string | null;
  [k: string]: unknown;
};

export type HistoryEntry = {
  name?: string;
  sport_id?: string | null;
  role?: string | null;
  email?: string | null;
  phone?: string | null;
  status?: string;
  moved_to_school_name?: string | null;
  departed_at?: string;
  notes?: string | null;
};

export type School = {
  placeId?: string;
  ghostfit_id?: string;
  name?: string;
  city?: string;
  county?: string;
  state?: string;
  state_code?: string;
  mascot?: string;
  primary_color?: string;
  secondary_color?: string;
  phone?: string;
  address?: string;
  zip?: string;
  website?: string;
  athletic_website?: string;
  enrollment?: number | string;
  classification?: string;
  conference?: string;
  district?: string;
  data_quality_score?: number;
  fundraiser_readiness?: string;
  readiness_blockers?: string[];
  logo_status?: string;
  athletic_director?: AthleticDirector | null;
  contacts?: Coach[];
  coaches?: Coach[];
  coach_history?: HistoryEntry[];
  ad_history?: HistoryEntry[];
  sports_offered?: string[];
  last_verified_at?: string;
};

// scout-dashboard QualityBar rule: scores ≤ 1 are the 0–1 pipeline scale.
export function qualityPct(score?: number): number {
  const raw = score || 0;
  return raw <= 1 ? Math.round(raw * 100) : Math.round(raw);
}

export const READINESS: Record<string, { label: string; cls: string; dot: string }> = {
  ready: { label: 'Ready', cls: 'bg-success/10 text-success', dot: '#34c759' },
  needs_logo: { label: 'Needs Logo', cls: 'bg-warning/10 text-warning', dot: '#ff9500' },
  needs_contacts: { label: 'Needs Contacts', cls: 'bg-warning/10 text-warning', dot: '#ff9500' },
  not_ready: { label: 'Not Ready', cls: 'bg-bg text-muted', dot: '#86868b' },
};

// Same fallback derivation scout-dashboard uses when a doc predates
// recalcReadiness: infer from the quality score.
export function readinessOf(s: School): string {
  if (s.fundraiser_readiness && READINESS[s.fundraiser_readiness]) return s.fundraiser_readiness;
  const pct = qualityPct(s.data_quality_score);
  return pct >= 60 ? 'ready' : pct >= 30 ? 'needs_logo' : 'not_ready';
}

// scout-dashboard reads coach lists as contacts || coaches everywhere.
export const coachesOf = (s: School): Coach[] => s.contacts || s.coaches || [];

export const stateOf = (s: School): string => s.state_code || s.state || '';
